/**
 * Single source of truth for the ER / ESA "selected domain" storage keys, their
 * All-domains sentinels, and the resolved selection each module's React context
 * publishes.
 *
 * WHY THIS MODULE EXISTS (HFV3-BACKEND-P)
 * ---------------------------------------
 * Each module's domain context persists a SENTINEL (`__er_all_domains__`) for
 * an explicit "All domains" choice, so it can be told apart from `null` = "no
 * choice yet → fall back to the workspace default". That is correct and
 * load-bearing.
 *
 * The bug was in the api layer: it re-declared the storage key and read
 * localStorage RAW, so the sentinel shipped as `x-domain-id`. The backend bound
 * it into `domains.id` (a uuid column), Postgres raised 22P02, and every
 * authenticated ER route 500'd for any multi-domain workspace whose operator
 * had clicked "All domains".
 *
 * ESA hit the identical bug one day later and fixed it locally; nobody
 * backported it to ER. Two copies of one rule is how that happens, so both
 * modules now share this definition.
 *
 * Reading raw localStorage had a SECOND, invisible failure mode: it also
 * bypassed the context's staleness check (`domains.some(d => d.id === stored)`).
 * A deleted domain, or one belonging to another workspace, shipped on the wire;
 * the backend guard resolved nothing and threw `ForbiddenException`, so the
 * whole module 403'd while the selector visibly showed the default domain. That
 * never appeared in Sentry, because a 403 is not a 500. Publishing the RESOLVED
 * selection here is what closes it.
 *
 * Deliberately NOT a `'use client'` module and deliberately React-free: it is
 * imported by `lib/er/api.ts` and `lib/esa/api.ts`, and pulling the context in
 * directly would drag React + TanStack into their module graphs.
 */

export const ER_DOMAIN_STORAGE_KEY = 'er:selectedDomainId'
export const ER_ALL_DOMAINS_SENTINEL = '__er_all_domains__'

export const ESA_DOMAIN_STORAGE_KEY = 'esa:selectedDomainId'
export const ESA_ALL_DOMAINS_SENTINEL = '__esa_all_domains__'

/** Modules whose API surface is narrowed by an `x-domain-id` header. */
export type DomainScopedModule = 'er' | 'esa'

const MODULE_CONFIG: Record<
  DomainScopedModule,
  { storageKey: string; sentinel: string }
> = {
  er: { storageKey: ER_DOMAIN_STORAGE_KEY, sentinel: ER_ALL_DOMAINS_SENTINEL },
  esa: {
    storageKey: ESA_DOMAIN_STORAGE_KEY,
    sentinel: ESA_ALL_DOMAINS_SENTINEL,
  },
}

/**
 * The selection each module's provider has RESOLVED against its loaded domain
 * list. Absent from the map = "no provider mounted"; present-but-null =
 * "explicitly All domains". The distinction matters: a mounted provider that
 * resolved to null must NOT fall back to localStorage.
 */
const resolvedByModule = new Map<DomainScopedModule, string | null>()

/** Called by the module's domain context whenever its resolved selection changes. */
export function publishResolvedDomainId(
  module: DomainScopedModule,
  domainId: string | null,
): void {
  resolvedByModule.set(module, domainId)
}

/** Called when the provider unmounts, so requests fall back to localStorage. */
export function clearResolvedDomainId(module: DomainScopedModule): void {
  resolvedByModule.delete(module)
}

/**
 * The concrete domain id to send as `x-domain-id`, or `null` for
 * workspace-wide.
 *
 * Prefers the provider's resolved selection. The localStorage read is the
 * SSR / provider-not-yet-mounted fallback, and strips the sentinel — so it is
 * never worse than the old behaviour, and correct where the old code was not.
 */
export function readSelectedDomainId(
  module: DomainScopedModule,
): string | null {
  if (resolvedByModule.has(module)) return resolvedByModule.get(module) ?? null
  if (typeof window === 'undefined') return null

  const { storageKey, sentinel } = MODULE_CONFIG[module]
  try {
    const value = window.localStorage.getItem(storageKey)
    if (!value || !value.trim() || value === sentinel) return null
    return value
  } catch {
    // Storage can throw outright (Safari private mode, disabled cookies).
    return null
  }
}
